<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <div class="page-header">
        <h1>
          {{ $t('shop.results.importTitle') }}
        </h1>
      </div>
      <div>
        <button
          type="button"
          aria-label="Close"
          class="close"
          @click="$emit('close')"
        >
          ×
        </button>
      </div>
    </div>
    <hr class="mt-0">

    <div
      v-if="!pending"
      class="d-flex"
    >
      <FileUpload
        class="upload mr-4"
        @upload="validate"
      />
    </div>
    <div
      v-else
      class="upload"
    >
      <Loader />
    </div>

    <div
      v-if="uploadError"
      class="text-danger"
    >
      {{ $t('shop.error.UnableToParse') }}
    </div>

    <div
      v-if="validationResult"
      class="validation-result"
    >
      <div class="d-flex align-items-center justify-content-between">
        <div
          v-if="valid"
          class="my-4 d-flex align-items-center"
        >
          <i class="fa-regular file-icon fa-file-circle-check text-primary pr-3" />
          <div class="font-weight-bold text-primary">
            {{ $t('import.noIssues') }}
          </div>
        </div>
        <div
          v-else
          class="my-4 d-flex align-items-center"
        >
          <i class="fa-regular file-icon fa-file-circle-info text-warning pr-3" />
          <div class="font-weight-bold text-warning">
            {{ $t('import.thereAreSomeIssues') }}
          </div>
        </div>
        <div>
          <button
            v-if="!valid"
            class="btn btn-sm btn-secondary mt-2 mr-1"
            :disabled="importPending"
            @click="validate(file)"
          >
            {{ $t('shop.revalidate') }}
            <i class="fas fa-rotate-right pl-2" />
          </button>
          <button
            class="btn btn-sm btn-primary mt-2"
            :disabled="importPending || validationResult.rows.length === 0"
            @click="upload"
          >
            {{ $t('shop.confirmImport') }}
            <i class="fa-regular fa-arrow-right pl-2" />
          </button>
        </div>
      </div>

      <div v-if="validationResult.errors.length > 0">
        <div class="section-title">
          {{ $t('import.errors') }}
        </div>
        <div
          v-for="(error, i) in validationResult.errors"
          :key="`error_${i}`"
        >
          <ShopImportError v-bind="error" />
        </div>
        <hr>
      </div>

      <template v-if="newRows.length > 0">
        <div class="section-title">
          {{ $t('import.new') }}
        </div>
        <ResultsImportRows :rows="newRows" />
        <hr>
      </template>

      <template v-if="updatedRows.length > 0">
        <div class="section-title">
          {{ $t('import.updated') }}
        </div>
        <ResultsImportRows
          :rows="updatedRows"
          show-old
        />
        <hr>
      </template>

      <template v-if="unchangedRows.length > 0">
        <div class="section-title">
          {{ $t('import.unchanged') }}
        </div>
        <ResultsImportRows
          style="opacity: 0.7"
          :rows="unchangedRows"
        />
      </template>
    </div>
  </div>
</template>

<script>
import FileUpload from '@/components/FileUpload';
import ShopImportError from '@/components/shop/ShopImportError';
import { mapActions } from 'vuex';
import ResultsImportRows from './ResultsImportRows';

export default {
  props: {
    catalogId: String,
  },
  data: () => ({
    pending: false,
    importPending: false,
    validationResult: null,
    file: null,
    uploadError: false,
  }),
  components: {
    FileUpload,
    ShopImportError,
    ResultsImportRows,
  },
  computed: {
    valid() {
      return !!this.validationResult && this.validationResult.errors.length === 0;
    },
    newRows() {
      if (!this.validationResult) return [];
      return this.validationResult.rows.filter((x) => x.oldScore == null);
    },
    updatedRows() {
      if (!this.validationResult) return [];
      return this.validationResult.rows.filter((x) => x.oldScore != null
        && (x.oldScore !== x.newScore || (x.oldNote || '') !== (x.newNote || '')));
    },
    unchangedRows() {
      if (!this.validationResult) return [];
      return this.validationResult.rows.filter((x) => x.oldScore != null
        && x.oldScore === x.newScore && (x.oldNote || '') === (x.newNote || ''));
    },
  },
  methods: {
    ...mapActions('shop', [
      'validateResultsImport',
      'uploadResultsImport',
    ]),
    validate(file) {
      this.uploadError = false;
      const formData = new FormData();
      this.file = file;
      this.validationResult = null;
      formData.append('file', file);

      this.pending = true;
      this.validateResultsImport({
        params: {
          catalogId: this.catalogId,
        },
        data: formData,
      })
        .then(({ data }) => {
          this.validationResult = data;
        })
        .catch(() => {
          this.uploadError = true;
        })
        .finally(() => {
          this.pending = false;
        });
    },
    upload() {
      this.importPending = true;
      this.uploadError = false;
      const formData = new FormData();
      formData.append('file', this.file);

      this.uploadResultsImport({
        params: {
          catalogId: this.catalogId,
        },
        data: formData,
      })
        .then(() => {
          this.$emit('imported');
        })
        .catch(() => {
          this.uploadError = true;
        })
        .finally(() => {
          this.importPending = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.file-icon {
  font-size: 35px;
}

.upload {
  min-height: 20px;
  padding: 5px 15px;
  max-width: 700px;
  margin: 0;
}

.upload ::v-deep .dz-default {
  margin: 1em 0;
}

.section-title {
  font-weight: 500;
  font-size: 15px;
}

.validation-result {
  height: calc(100vh - 370px);
  overflow: auto;
}

.close {
  font-size: 40px;
}
</style>
